import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, PackagePlus, Pencil, Printer, X } from 'lucide-react'
import { toast } from 'sonner'

import type { ScentingLotInReason } from '@/lib/api/scenting-lot-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    WarehouseNoteStatusBadge,
    warehouseNoteStatusLabel,
} from '@/components/warehouse/note-status-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useApproveScentingInNote,
    useCancelScentingInNote,
    useRejectScentingInNote,
    useRollbackScentingInNote,
    useScentingInNote,
    useUpdateScentingInNoteInfo,
} from '@/hooks/use-scenting-lot-query'
import {
    NoteRollbackButton,
    NoteRollbackLinks,
} from '@/components/warehouse/note-rollback'
import { ScentingInNoteInfoDialog } from '@/components/warehouse/scenting-in-note-info-dialog'
import { usePrintScentingLotLabel } from '@/hooks/use-lot-label-print'
import { formatDateTimeVN, formatDateVN, formatQty } from '@/lib/format'
import { StockInNotePrintButton } from '@/components/warehouse/stock-in-note-print-button'
import { scentingLotInNoteToPrintData } from '@/lib/print-adapters/stock-in-note-adapters'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-in-notes/$id',
)({
    component: StockNoteDetailPage,
})

export function StockNoteDetailPage() {
    const { id } = Route.useParams()
    const { data, isLoading } = useScentingInNote(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApproveScentingInNote()
    const reject = useRejectScentingInNote()
    const cancel = useCancelScentingInNote()
    const rollback = useRollbackScentingInNote()
    const printLot = usePrintScentingLotLabel()
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')
    const [infoOpen, setInfoOpen] = useState(false)
    const updateInfo = useUpdateScentingInNoteInfo()
    const approveWorkflow = useWorkflowTracking('scenting_stock_in', {
        operation: 'approve',
        attemptKey: id,
        autoStart: false,
    })
    const rejectWorkflow = useWorkflowTracking('scenting_stock_in', {
        operation: 'reject',
        attemptKey: id,
        active: rejectOpen,
    })
    const cancelWorkflow = useWorkflowTracking('scenting_stock_in', {
        operation: 'cancel',
        attemptKey: id,
        autoStart: false,
    })

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isPending = note.status === 'pending'
    // Phiếu đã duyệt vẫn sửa được 2 trường thuần mô tả (ghi chú + ngày lấy hương).
    const isApproved = note.status === 'approved'
    const isCreator = user?.id && note.created_by_id === user.id
    // Quyền phải khớp ĐÚNG quyền route backend đòi cho kho LẤY HƯƠNG
    // (scenting_lots.*). Trước đây màn này đọc nhầm quyền kho PHÂN LOẠI
    // (sorting_lots.*): người đúng quyền không thấy nút, người sai quyền bấm vào
    // thì ăn 403 từ server.
    const canApprove = hasPermission?.('scenting_lots.approve_in') ?? true
    const canWrite = hasPermission?.('scenting_lots.write') ?? true

    const handleApprove = async () => {
        approveWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_approve_in())) {
            approveWorkflow.finish('closed')
            return
        }
        approveWorkflow.step('warning_confirmed')
        try {
            await approveWorkflow.run(() => approve.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : m.sl_detail_error_approve(),
            )
        }
    }

    const handleReject = async () => {
        try {
            await rejectWorkflow.run(() =>
                reject.mutateAsync({ id, reason: rejectReason }),
            )
            setRejectOpen(false)
            setRejectReason('')
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_reject(),
            )
        }
    }

    const handleCancel = async () => {
        cancelWorkflow.step('warning_shown')
        if (!confirm(m.sl_detail_confirm_cancel_in())) {
            cancelWorkflow.finish('closed')
            return
        }
        cancelWorkflow.step('warning_confirmed')
        try {
            await cancelWorkflow.run(() => cancel.mutateAsync(id))
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    const handleUpdateInfo = async (values: {
        scenting_date: string
        notes: string
    }) => {
        try {
            await updateInfo.mutateAsync({ id, req: values })
            setInfoOpen(false)
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PageBackButton asChild>
                                <Link
                                    to="/scenting-warehouse/stock-in-notes"
                                    search={{ page: 1 }}
                                >
                                    <ArrowLeft className="size-4" />
                                </Link>
                            </PageBackButton>
                            <PackagePlus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <WarehouseNoteStatusBadge status={note.status} />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <StockInNotePrintButton
                                loadNote={() => note}
                                toPrintData={(n) =>
                                    scentingLotInNoteToPrintData(n, {
                                        warehouseLabel: m.sinp_wh_scenting(),
                                        reasonLabel: reasonLabel(n.reason),
                                        statusLabel: warehouseNoteStatusLabel(
                                            n.status,
                                        ),
                                    })
                                }
                            />
                            {isApproved &&
                                canWrite &&
                                !note.stocktake_session_id && (
                                    <Button
                                        variant="outline"
                                        className="h-11 md:h-9"
                                        onClick={() => setInfoOpen(true)}
                                    >
                                        <Pencil className="size-4 mr-1" />
                                        {m.scl_in_info_edit_btn()}
                                    </Button>
                                )}
                            {isPending && (
                                <>
                                    {isCreator &&
                                        canWrite &&
                                        !note.stocktake_session_id && (
                                            <Button
                                                asChild
                                                variant="outline"
                                                className="h-11 md:h-9"
                                            >
                                                <Link
                                                    to="/scenting-warehouse/stock-in-notes/$id/edit"
                                                    params={{ id }}
                                                >
                                                    <Pencil className="size-4 mr-1" />
                                                    {m.pl_pnk_edit_btn()}
                                                </Link>
                                            </Button>
                                        )}
                                    {canApprove && (
                                        <>
                                            <Button
                                                className="h-11 md:h-9"
                                                onClick={handleApprove}
                                                disabled={approve.isPending}
                                            >
                                                <Check className="size-4 mr-1" />{' '}
                                                {m.sl_detail_action_approve()}
                                            </Button>
                                            <Button
                                                className="h-11 md:h-9"
                                                variant="destructive"
                                                onClick={() =>
                                                    setRejectOpen(true)
                                                }
                                            >
                                                <X className="size-4 mr-1" />{' '}
                                                {m.sl_detail_action_reject()}
                                            </Button>
                                        </>
                                    )}
                                    {isCreator && canWrite && (
                                        <Button
                                            className="h-11 md:h-9"
                                            variant="outline"
                                            onClick={handleCancel}
                                            disabled={cancel.isPending}
                                        >
                                            {m.sl_detail_action_cancel()}
                                        </Button>
                                    )}
                                </>
                            )}
                            <NoteRollbackButton
                                noteId={note.id}
                                status={note.status}
                                canRollback={canApprove}
                                rolledBackBy={note.rolled_back_by}
                                rollbackOf={note.rollback_of}
                                confirmMessage={m.note_rollback_confirm_in()}
                                pending={rollback.isPending}
                                onRollback={() => rollback.mutateAsync(id)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={reasonLabel(note.reason)}
                        />
                    </div>
                    {note.sorting_lot_stock_out_note_id && (
                        <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">
                                {m.sl_detail_label_source_pxk_scenting()}
                            </p>
                            <Link
                                to="/sorting-warehouse/stock-out-notes/$id"
                                params={{
                                    id: note.sorting_lot_stock_out_note_id,
                                }}
                                className="font-mono text-base font-medium hover:underline md:text-sm"
                            >
                                {note.sorting_lot_stock_out_note_code ??
                                    note.sorting_lot_stock_out_note_id}
                            </Link>
                        </div>
                    )}
                    {note.scenting_date && (
                        <Field
                            label={m.scl_new_scenting_date_label()}
                            value={formatDateVN(note.scenting_date)}
                        />
                    )}
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={
                                    note.status === 'approved'
                                        ? m.sl_detail_label_approver()
                                        : m.sl_detail_label_rejecter()
                                }
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                    <NoteRollbackLinks
                        rolledBackBy={note.rolled_back_by}
                        rollbackOf={note.rollback_of}
                        renderLink={(ref) => (
                            <Link
                                to="/scenting-warehouse/stock-out-notes/$id"
                                params={{ id: ref.id }}
                                className="text-primary hover:underline"
                            >
                                {ref.code}
                            </Link>
                        )}
                    />
                </CardContent>
            </Card>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>{m.sl_lot_grade()}</TableHead>
                            <TableHead>{m.scl_detail_col_source()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_detail_col_in()}
                            </TableHead>
                            <TableHead className="text-right w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-sm">
                                    {it.splits && it.splits.length > 0 ? (
                                        <div className="space-y-0.5">
                                            <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                                                {m.lot_items_split_badge({
                                                    n: it.splits.length,
                                                })}
                                            </span>
                                            {it.splits.map((s, i) => (
                                                <div
                                                    key={s.id ?? i}
                                                    className="text-muted-foreground"
                                                >
                                                    {s.new_lot_code ||
                                                        m.lot_items_split_auto_code()}
                                                    {' · '}
                                                    {formatQty(s.quantity)}kg
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Link
                                            to="/scenting-warehouse/lots/$id"
                                            params={{ id: it.scenting_lot_id }}
                                            className="text-primary hover:underline"
                                        >
                                            {it.scenting_lot_code}
                                        </Link>
                                    )}
                                </TableCell>
                                <TableCell>{it.grade_name ?? '—'}</TableCell>
                                <TableCell className="text-sm">
                                    {(it.sources ?? []).length > 0 ? (
                                        <div className="space-y-0.5">
                                            {(it.sources ?? []).map((s) => (
                                                <div
                                                    key={s.id}
                                                    className="font-mono"
                                                >
                                                    {s.source_sorting_lot_code ??
                                                        s.source_sorting_lot_id}
                                                    <span className="text-muted-foreground">
                                                        {' · '}
                                                        {formatQty(
                                                            s.source_quantity,
                                                        )}
                                                        kg
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={
                                            note.status !== 'approved'
                                                ? m.lot_label_print_btn_disabled_pending_approve()
                                                : m.lot_label_print_btn_title()
                                        }
                                        title={
                                            note.status !== 'approved'
                                                ? m.lot_label_print_btn_disabled_pending_approve()
                                                : m.lot_label_print_btn_title()
                                        }
                                        disabled={
                                            printLot.isPending ||
                                            note.status !== 'approved'
                                        }
                                        onClick={() =>
                                            printLot.mutate(it.scenting_lot_id)
                                        }
                                    >
                                        <Printer className="size-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {infoOpen && (
                <ScentingInNoteInfoDialog
                    open={infoOpen}
                    onOpenChange={setInfoOpen}
                    scentingDate={note.scenting_date}
                    notes={note.notes}
                    isPending={updateInfo.isPending}
                    onSubmit={handleUpdateInfo}
                />
            )}

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.sl_detail_dialog_reject_title_in()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>{m.sl_detail_dialog_reject_label()}</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => {
                                rejectWorkflow.interact('details')
                                setRejectReason(e.target.value)
                            }}
                            placeholder={m.sl_detail_dialog_reject_placeholder()}
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={() => setRejectOpen(false)}
                        >
                            {m.sl_detail_dialog_cancel_btn()}
                        </Button>
                        <Button
                            variant="destructive"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={handleReject}
                            disabled={reject.isPending}
                        >
                            {m.sl_detail_dialog_confirm_reject_btn()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function reasonLabel(reason: ScentingLotInReason): string {
    if (reason === 'from_process') return m.scl_in_reason_from_process()
    if (reason === 'return_from_packaging_process')
        return m.scl_in_reason_return_from_packaging_process()
    return m.scl_in_reason_adjustment()
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
